import { PackageGroupFulfillmentStatus } from '../store/config/enums/warehouseWork.enum';
import { PackageGroup, Product } from '../store/config/types';
import {
  AssemblyBuild,
  InventoryCountOrder,
  FulfillmentItem,
  FulfillmentSalesOrderStruct,
  ItemPendingFulfillmentStruct,
  ItemReceipt,
  Vendor,
  WorkorderUpdateStruct,
} from '../store/config/types/warehouseWork.types';
import { packageGroupToFulfillmentSalesOrder } from '../utils/fulfillment.helpers';
import axios from './axios';

function filterToQuery(filters: any) {
  let query: string = '';
  Object.keys(filters)
    .filter((filterName) => filters[filterName])
    .forEach((filterName, index) => {
      query += `${index == 0 ? '?' : '&'}${filterName}=${filters[filterName]}`;
    });
  return encodeURI(query);
}

async function fetchItemsPendingFulfillment(filters: any): Promise<ItemPendingFulfillmentStruct[]> {
  const res = await axios.get(encodeURI(`/warehouse-work/items-pending-fulfillment${filterToQuery(filters)}`));
  return res.data.data as ItemPendingFulfillmentStruct[];
}

async function fetchFulfillmentOrders(date: string) {
  const res = await axios.get(`/warehouse-work/sales-order/${date}`);
  const salesOrderPendingFulfillment: FulfillmentSalesOrderStruct[] = [];
  const salesOrderStatusTBD: FulfillmentSalesOrderStruct[] = [];

  res.data.data.forEach((packageGroup: PackageGroup) => {
    packageGroup.orderItems?.sort((a, b) => +(a.additionalData?.lineIndex ?? 0) - +(b.additionalData?.lineIndex ?? 0));
    switch (packageGroup.fulfillment?.status) {
      case PackageGroupFulfillmentStatus.FULFILLED:
      case PackageGroupFulfillmentStatus.EXTERNALLY_FULFILLED:
      case PackageGroupFulfillmentStatus.PENDING:
      case PackageGroupFulfillmentStatus.COMMITTED:
      case PackageGroupFulfillmentStatus.PROCESSING:
      case PackageGroupFulfillmentStatus.SYNCED:
      case PackageGroupFulfillmentStatus.NEW:
        salesOrderPendingFulfillment.push(packageGroupToFulfillmentSalesOrder(packageGroup));
        break;
      default:
        salesOrderStatusTBD.push(packageGroupToFulfillmentSalesOrder(packageGroup));
    }
  });
  return { salesOrderPendingFulfillment, salesOrderStatusTBD };
}

async function fetchAssembliesPendingBuild(filters: any): Promise<ItemPendingFulfillmentStruct[]> {
  const res = await axios.get(encodeURI(`/warehouse-work/assemblies-pending-build${filterToQuery(filters)}`));
  return res.data.data as ItemPendingFulfillmentStruct[];
}

// DEPRICATED
async function changeDefaultLot(allWorkOrderIds: number[], productId: string | number, inventoryLotQuantityId: number) {
  const res = await axios.patch(encodeURI(`/warehouse-work/default-lot`), {
    allWorkOrderIds,
    productId,
    inventoryLotQuantityId,
  });
  return res.data.data;
}

async function skipFulfillment(packageGroupId: number) {
  const res = await axios.patch(`/warehouse-work/sales-order/skip`, { packageGroupId });
  return res.data.data;
}

async function convertFulfillment(packageGroupId: number[], convertTo: string) {
  const res = await axios.patch(`/warehouse-work/sales-order/convert`, { packageGroupId, convertTo });
  return Boolean(res.data.data?.successfulIds?.length);
}
async function syncFulfillment(packageGroupId: number) {
  const res = await axios.patch(`/warehouse-work/sales-order/sync`, { packageGroupId });
  return res.data;
}

async function syncWorkOrder(assemblyBuildId: number) {
  const res = await axios.patch(`/warehouse-work/assembly-build-workorders/sync`, { assemblyBuildId });
  return res.data;
}

async function convertToPending(assemblyBuildId: number | number[]) {
  const res = await axios.patch(`/warehouse-work/assembly-build/convert`, { assemblyBuildId, convertTo: 'PENDING' });
  return res.data;
}

async function getAvailableLotsFor(productId: number, warehouseIds: number[], minQuantity: number) {
  const res = await axios.get(`/warehouse-work/available-lots/${productId}?min=${minQuantity}`);

  return res.data.data;
}

async function updatePackageGroupLots(packageGroupFulfillmentId: number, fulfillmentItems?: FulfillmentItem[]) {
  const res = await axios.patch(encodeURI(`/warehouse-work/fulfillment-items`), {
    packageGroupFulfillmentId,
    fulfillmentItems,
  });
  return res.data.data;
}

async function updateWorkorderLots(updates: WorkorderUpdateStruct[]) {
  console.log(updates);

  const res = await axios.patch(encodeURI(`/warehouse-work/lots`), updates);
  return res.data.data;
}

async function fetchAssemblyBuildWorkorders(filters: any): Promise<AssemblyBuild[]> {
  const res = await axios.get(encodeURI(`/warehouse-work/assembly-build-workorders${filterToQuery(filters)}`));
  return res.data.data as AssemblyBuild[];
}

async function createAssemblyWorkorder(
  warehouseId: number,
  productId: number,
  assemblyCount: number,
  lotId: string,
  date: string,
) {
  const res = await axios.post(encodeURI(`/warehouse-work/assembly-build-workorders`), {
    warehouseId,
    productId,
    assemblyCount,
    lotId,
    date,
  });
  return res.data.data;
}

async function changeLotOfAssemblyItem(assemblyBuildItemIds: number[], lotId: string) {
  const res = await axios.patch(encodeURI(`/warehouse-work/assembly-build-item`), {
    assemblyBuildItemIds,
    lotId,
  });
  return res.data.ok;
}

async function getAssemblyTotalQuantities(productId: number, date?: string) {
  const res = await axios.get(
    encodeURI(`/warehouse-work/assembly-build/${productId}/quantities${date ? '?date=' + date : ''}`),
  );
  return res.data.data;
}

async function getWmsUsers() {
  const res = await axios.get(encodeURI(`/warehouse-work/users`));
  return res.data.data;
}
async function saveWmsUser(user: any) {
  const res = await (user.warehouseUserId ? axios.patch : axios.post)(`/warehouse-work/users`, user);
  return res.data.data;
}
async function removeWmsUser(user: any) {
  const res = await axios.delete(`/warehouse-work/users/${user.warehouseUserId}`);
  return res.data.data;
}

async function getApiQueueInfo() {
  const res = await axios.get(`/warehouse-work/api-queue-info`);
  return res.data.data;
}

async function fetchItemReceipts(info?: any) {
  const res = await axios.get(`/warehouse-work/item-receipts`, info ? { params: info } : undefined);
  const itemReceipts = res.data.data as ItemReceipt[];
  itemReceipts.forEach((ir) => {
    ir.items?.sort((ir1, ir2) => {
      if (ir1.productId === ir2.productId) {
        return (ir1.lotId ?? '').localeCompare(ir2.lotId ?? '');
      }
      return ir1.productId - ir2.productId;
    });
  });
  return itemReceipts;
}

async function approveItemReceipts(itemReceiptId: number | number[]) {
  const res = await axios.post(`/warehouse-work/item-receipts/approve`, { itemReceiptId });
  return res.data.data as ItemReceipt[];
}
async function rejectItemReceipts(itemReceiptId: number | number[]) {
  const res = await axios.post(`/warehouse-work/item-receipts/reject`, { itemReceiptId });
  return res.data.data as ItemReceipt[];
}

async function tryToSyncItemReceipts(itemReceiptId: number | number[]) {
  const res = await axios.post(`/warehouse-work/item-receipts/try-to-sync`, { itemReceiptId });
  return res.data.data as ItemReceipt[];
}

async function changeItemReceiptItemLot(lotInfo: { lotId: string; itemReceiptItemId: number }) {
  const res = await axios.post(`/warehouse-work/item-receipts/change-item-lot`, lotInfo);
  return res.data.data as ItemReceipt[];
}
async function changeItemReceiptWarehouse(info: { itemReceiptId: number | number[]; warehouseId: number }) {
  const res = await axios.post(`/warehouse-work/item-receipts/change-warehouse`, info);
  return res.data.data as ItemReceipt[];
}
async function changeItemReceiptVendor(info: { itemReceiptId: number; vendorId: number; vendor: string }) {
  const res = await axios.patch(`/wms/item-receipts`, info);
  return res.data.data;
}

async function fetchInventoryCountOrders(date: string) {
  const res = await axios.get(`/warehouse-work/inventory-count-orders?date=${date}`);
  return res.data.data as InventoryCountOrder[];
}
async function createInventoryCountOrder(order: Partial<InventoryCountOrder>) {
  const res = await axios.post(`/warehouse-work/inventory-count-orders`, order);
  return res.data.data as any;
}
async function cancelInventoryCountOrder(inventoryCountOrderId: number | number[]) {
  const res = await axios.patch(`/warehouse-work/inventory-count-orders/cancel`, { inventoryCountOrderId });
  return res.data.data as any;
}
async function approveInventoryCountOrder(inventoryCountOrderId: number | number[]) {
  const res = await axios.patch(`/warehouse-work/inventory-count-orders/approve`, { inventoryCountOrderId });
  return res.data.data as any;
}
async function approveInventoryCountOrderSync(inventoryCountOrderId: number | number[]) {
  const res = await axios.patch(`/warehouse-work/inventory-count-orders/approve-and-sync`, { inventoryCountOrderId });
  return res.data.data as any;
}
async function tryToSyncInventoryCountOrder(inventoryCountOrderId: number | number[]) {
  const res = await axios.patch(`/warehouse-work/inventory-count-orders/try-to-sync`, { inventoryCountOrderId });
  return res.data.data as any;
}
async function approveInventoryCountRow(rows: any[]) {
  const rowUpdatePayload: any = {
    inventoryCountOrderId: rows[0].inventoryCountOrderId,
    productId: rows[0].productId,
    handlingUnitGroupIds: rows.map((row) => row.handlingUnitGroupId),
    status: 'APPROVED',
  };
  const res = await axios.patch(`/warehouse-work/inventory-count-orders/update-row-status`, rowUpdatePayload);
  return res.data.data as any;
}
async function rejectInventoryCountRow(rows: any[]) {
  const rowUpdatePayload: any = {
    inventoryCountOrderId: rows[0].inventoryCountOrderId,
    productId: rows[0].productId,
    handlingUnitGroupIds: rows.map((row) => row.handlingUnitGroupId),
    status: 'REJECTED',
  };
  const res = await axios.patch(`/warehouse-work/inventory-count-orders/update-row-status`, rowUpdatePayload);
  return res.data.data as any;
}
async function clearInventoryCountRow(rows: any[]) {
  const rowUpdatePayload: any = {
    inventoryCountOrderId: rows[0].inventoryCountOrderId,
    productId: rows[0].productId,
    handlingUnitGroupIds: rows.map((row) => row.handlingUnitGroupId),
    status: 'NEW',
  };
  const res = await axios.patch(`/warehouse-work/inventory-count-orders/update-row-status`, rowUpdatePayload);
  return res.data.data as any;
}
async function rejectInventoryCountOrder(inventoryCountOrderId: number | number[]) {
  const res = await axios.patch(`/warehouse-work/inventory-count-orders/reject`, { inventoryCountOrderId });
  return res.data.data as any;
}
async function refreshSnapshotInventoryCountOrder(inventoryCountOrderId: number | number[]) {
  const res = await axios.patch(`/warehouse-work/inventory-count-orders/refresh-snapshot`, { inventoryCountOrderId });
  return res.data.data as any;
}

async function searchProduct(query: string) {
  const res = await axios.get(`/wms/search-product?query=${query}`);
  return res.data.data as Product[];
}
async function searchVendor(query: string) {
  const res = await axios.get(`/wms/search-vendor?query=${query}`);
  return res.data.data as Vendor[];
}
async function searchProductLot(query: string, warehouseId?: number, productId?: number) {
  const res = await axios.get(
    `/wms/search-product-lot?warehouseId=${warehouseId ?? ''}&productId=${productId ?? ''}&query=${query}`,
  );
  return res.data.data as any[];
}

async function getAllAvailableProductsOfWarehouse(warehouseId?: number) {
  const params: any = {};
  if (warehouseId) {
    params.warehouseId = warehouseId;
  }
  const res = await axios.get(`/warehouse-work/get-available-products`, { params });
  return res.data.data as Product[];
}

export const ItemReceiptService = {
  fetchItemReceipts,
  approveItemReceipts,
  rejectItemReceipts,
  tryToSyncItemReceipts,
  changeItemReceiptItemLot,
  changeItemReceiptWarehouse,
  changeItemReceiptVendor,
};

export const FulfillmentService = {
  fetchItemsPendingFulfillment,
  fetchFulfillmentOrders,
  changeDefaultLot,
  updatePackageGroupLots,
  skipFulfillment,
  convertFulfillment,
  syncFulfillment,
  getAvailableLotsFor,
};

export const AssemblyBuildService = {
  fetchAssemblyBuildWorkorders,
  fetchAssembliesPendingBuild,
  createAssemblyWorkorder,
  changeLotOfAssemblyItem,
  syncWorkOrder,
  getAssemblyTotalQuantities,
  convertToPending,
};

export const InventoryCountService = {
  fetchInventoryCountOrders,
  createInventoryCountOrder,
  cancelInventoryCountOrder,
  approveInventoryCountOrder,
  approveInventoryCountOrderSync,
  tryToSyncInventoryCountOrder,
  rejectInventoryCountOrder,
  refreshSnapshotInventoryCountOrder,
  approveInventoryCountRow,
  rejectInventoryCountRow,
  clearInventoryCountRow,
};

const WarehouseWorkService = {
  ...FulfillmentService,
  ...AssemblyBuildService,
  ...InventoryCountService,
  updateWorkorderLots,
  getWmsUsers,
  saveWmsUser,
  removeWmsUser,
  getApiQueueInfo,
  searchProduct,
  searchProductLot,
  searchVendor,
  getAllAvailableProductsOfWarehouse,
};
export default WarehouseWorkService;
